import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';

import { HTS_MASK } from '@helpers/custom-validator';
import { DEFAULT_CURRENCY_CODE } from '@constants/currency';
import { LineModel } from '@data-layer/models/entry-summaries';
import { CodeRepository } from '@data-layer/repositories';
import { CountryModel, StateModel } from '@data-layer/models/common';
import { EntrySummaryValidationHelper } from '@helpers/entry-summary-validation.helper';
import { enumDropdownActionIconColor, enumDropdownActionMenuMode } from '@shared/form-wrapper/enums';
import { WEIGHT_UNIT_KG } from '@constants/constants';
import { enumTransportationMode } from '@data-layer/enums/entry-summaries';
import { take } from 'rxjs/operators';

@Component({
  selector: 'es-invoice-summary-details-line-item',
  templateUrl: './es-invoice-summary-details-line-item.component.html',
  styleUrls: ['./es-invoice-summary-details-line-item.component.scss'],
})
export class EsInvoiceSummaryDetailsLineItemComponent implements OnInit, OnChanges {
  @Input() public transportationMode: enumTransportationMode;
  @Input() public index = 1;
  @Input() public item: LineModel = {} as LineModel;
  @Input() public currencyCode: string;
  @Input() public currencyRate: number = 1;
  @Input() public isCanEdit: boolean = true;
  @Input() public isCanView: boolean = true;
  // Multi-select mode (bulk delete): the kebab menu is replaced by a selection checkbox.
  @Input() public isSelectMode: boolean = false;
  @Input() public isSelected: boolean = false;

  @Output() public delete: EventEmitter<any> = new EventEmitter();
  @Output() public edit: EventEmitter<any> = new EventEmitter();
  @Output() public duplicate: EventEmitter<any> = new EventEmitter();
  @Output() public selectToggle: EventEmitter<void> = new EventEmitter<void>();

  public iconColor: enumDropdownActionIconColor = enumDropdownActionIconColor.DEFAULT;
  public menuMode: enumDropdownActionMenuMode = enumDropdownActionMenuMode.LIGHT;
  public open: boolean = false;
  public HTS_MASK: string = HTS_MASK;
  public DEFAULT_CURRENCY_CODE: string = DEFAULT_CURRENCY_CODE;
  public WEIGHT_UNIT_KG: string = WEIGHT_UNIT_KG;
  public isModalOpen = false;
  public countryOfOrigin: CountryModel;
  public countryOfExport: CountryModel;
  public stateOfOrigin: StateModel;
  public fieldsConfig: {
    [key: string]: {
      value: any;
      messages: string[];
      firstError: string;
      hasError: boolean;
      hasValue: boolean;
      shown: boolean;
    };
  } = {};

  constructor(private codeRepository: CodeRepository) {}

  public ngOnInit(): void {
    this.getCountryOfOrigin();
    this.getStateOfOrigin();
    this.getCountryOfExport();
    this.open = !!JSON.parse(localStorage.getItem('ES_ITEM_SUMMARY_IS_OPEN' + this.item.id));
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.item && this.item) {
      this.getFieldsConfig();
      if (!this.open) {
        this.open = EntrySummaryValidationHelper.getValidationMessages(this.item, this.transportationMode).length > 0;
      }
    }
  }

  public toggleOpen() {
    this.open = !this.open;
    localStorage.setItem('ES_ITEM_SUMMARY_IS_OPEN' + this.item.id, JSON.stringify(this.open));
  }

  public openModal() {
    this.isModalOpen = true;
  }

  public onDecline(): void {
    this.isModalOpen = false;
  }

  public onDelete() {
    this.isModalOpen = false;
    this.delete.emit(this.item);
  }

  public editItem() {
    this.edit.emit(this.item);
  }

  public duplicateItem() {
    this.duplicate.emit(this.item);
  }

  public getCountryOfOrigin() {
    if (!this.item || !this.item.countryOfOrigin) {
      return;
    }
    this.codeRepository
      .getCountry(this.item.countryOfOrigin.code)
      .pipe(take(1))
      .subscribe((country) => {
        this.countryOfOrigin = country;
      });
  }

  public getStateOfOrigin() {
    if (!this.item || !this.item.stateOfOrigin) {
      return;
    }
    this.codeRepository
      .getState(this.item.stateOfOrigin)
      .pipe(take(1))
      .subscribe((state) => {
        this.stateOfOrigin = state;
      });
  }

  public getCountryOfExport() {
    if (!this.item || !this.item.countryOfExport) {
      return;
    }
    this.codeRepository
      .getCountry(this.item.countryOfExport.code)
      .pipe(take(1))
      .subscribe((country) => {
        this.countryOfExport = country;
      });
  }

  private getFieldsConfig() {
    this.fieldsConfig = {};
    Object.keys(this.item).forEach((fieldName: string) => {
      const value = this.item[fieldName];
      const messages = EntrySummaryValidationHelper.itemMapValidation.has(fieldName)
        ? EntrySummaryValidationHelper.itemMapValidation.get(fieldName)(this.item)
        : [];
      this.fieldsConfig[fieldName] = {
        value,
        messages,
        firstError: messages && messages.length ? 'BACK_ERROR.' + messages[0] : null,
        hasError: !!(messages && messages.length),
        hasValue: !!value,
        shown: !!(messages && messages.length) || !!value,
      };
    });
  }
}
